import React from "react";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { Box, Typography, Paper, Card, CardContent } from "@mui/material";
import { areaElementClasses } from "@mui/x-charts/LineChart";
import { useLoaderData } from "react-router-dom";

// TypeScript interfaces
interface DataItem {
  creationTime: string;
}

interface DailyData {
  date: string;
  label: string;
  count: number;
}

interface ProcessedData {
  days: string[];
  counts: number[];
  fullData: DailyData[];
}

interface DailySparkLineChartProps {
  data?: DataItem[];
}

// Gradient bileşeni
const AreaGradient: React.FC<{ color: string; id: string }> = ({
  color,
  id,
}) => (
  <defs>
    <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor={color} stopOpacity={0.8} />
      <stop offset="100%" stopColor={color} stopOpacity={0.1} />
    </linearGradient>
  </defs>
);

const LogStatisticLineChart: React.FC<DailySparkLineChartProps> = () => {
  const { statistic = [] } = useLoaderData() as {
    statistic: { creationTime: string }[];
  };

  console.log(statistic);
  // Günlük gruplama fonksiyonu
  const processDailyData = (rawData: DataItem[]): ProcessedData => {
    const dailyCount: Record<string, DailyData> = {};

    rawData.forEach((item: DataItem) => {
      const date = new Date(item.creationTime);
      const dayKey = date.toISOString().split("T")[0]; // YYYY-MM-DD formatı
      const dayLabel = `${date.getDate().toString().padStart(2, "0")}/${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}`;

      if (!dailyCount[dayKey]) {
        dailyCount[dayKey] = {
          date: dayKey,
          label: dayLabel,
          count: 0,
        };
      }
      dailyCount[dayKey].count++;
    });

    // Sıralama ve son 30 gün
    const sortedData = Object.values(dailyCount)
      .sort(
        (a: DailyData, b: DailyData) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      .slice(-30); // Son 30 gün

    return {
      days: sortedData.map((item: DailyData) => item.label),
      counts: sortedData.map((item: DailyData) => item.count),
      fullData: sortedData,
    };
  };

  const { days, counts, fullData }: ProcessedData = processDailyData(statistic);
  const chartColor: string = "red";

  // İstatistikler
  const totalCount: number = counts.reduce(
    (sum: number, count: number) => sum + count,
    0
  );
  const avgDaily: string = (totalCount / counts.length).toFixed(1);
  const maxDaily: number = Math.max(...counts);

  return (
    <Card>
      <CardContent>
        <Typography
          component="h2"
          variant="subtitle2"
          gutterBottom
          color="primary"
        >
          Son 30 Gün - Hata dağılımı
        </Typography>

        <Box sx={{ height: 180, width: "100%", mb: 2 }}>
          <SparkLineChart
            data={counts}
            area
            showHighlight
            showTooltip
            xAxis={{
              scaleType: "band" as const,
              data: days,
            }}
            sx={{
              [`& .${areaElementClasses.root}`]: {
                fill: `url(#area-gradient-daily)`,
              },
            }}
          >
            <AreaGradient color={chartColor} id="area-gradient-daily" />
          </SparkLineChart>
        </Box>

        {/* Özet İstatistikler */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderRadius: 1,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Toplam
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: chartColor }}
            >
              {totalCount}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Günlük Ortalama
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: chartColor }}
            >
              {!isNaN(+avgDaily) ? avgDaily : 0}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              En Yüksek Gün
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: chartColor }}
            >
              {maxDaily == -Infinity ? 0 : maxDaily}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Aktif Gün Sayısı
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: chartColor }}
            >
              {counts.filter((count: number) => count > 0).length}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LogStatisticLineChart;

// Kullanım örneği:
// <DailySparkLineChart data={yourCreationTimeData} />
